import React, { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, CheckCircle, Clock, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [reviewAnswer, setReviewAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuestions();
      // Poll for updates every 10 seconds
      const interval = setInterval(fetchQuestions, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchQuestions = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/pending`),
        axios.get(`${API}/admin/all`)
      ]);
      setPendingQuestions(pendingRes.data);
      setAllQuestions(allRes.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        email: loginEmail,
        password: loginPassword
      });
      setIsAuthenticated(true);
      toast.success('Successfully logged in!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuestion = (question) => {
    setSelectedQuestion(question);
    setReviewAnswer(question.ai_answer || '');
  };

  const handleSubmitReview = async () => {
    if (!reviewAnswer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/review/${selectedQuestion.id}`, {
        reviewed_answer: reviewAnswer
      });
      toast.success('Review submitted successfully!');
      setSelectedQuestion(null);
      setReviewAnswer('');
      fetchQuestions();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginEmail('');
    setLoginPassword('');
    toast.success('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
      case 'pending_review':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" />Pending Review</Badge>;
      case 'answered':
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle className="w-3 h-3 mr-1" />Answered</Badge>;
      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8 animate-in fade-in zoom-in-95 duration-500">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Login</h2>
            <p className="text-slate-600">Access the review dashboard</p>
          </div>

          <Card className="p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  placeholder="andy.wood@flexproc.co.uk"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="Enter password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                />
              </div>

              <Button 
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-md py-5"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>
          </Card>

          <div className="text-center">
            <Button 
              data-testid="back-to-home-btn"
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Dashboard</h1>
              <p className="text-xs text-slate-500">Review & Manage Questions</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="home-link-btn"
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-sm text-slate-600 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={handleLogout}
              className="text-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total Questions</p>
                <p data-testid="total-questions" className="text-3xl font-bold text-slate-900">{allQuestions.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Pending Review</p>
                <p data-testid="pending-count" className="text-3xl font-bold text-amber-600">{pendingQuestions.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Answered</p>
                <p data-testid="answered-count" className="text-3xl font-bold text-emerald-600">{allQuestions.filter(q => q.status === 'answered').length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Review Interface */}
        {selectedQuestion ? (
          <Card className="p-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Review Question</h3>
              <Button 
                data-testid="back-to-list-btn"
                variant="outline"
                onClick={() => setSelectedQuestion(null)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">User Details</label>
                <div className="bg-slate-50 rounded-md p-4">
                  <p data-testid="review-user-name" className="text-sm"><span className="font-medium">Name:</span> {selectedQuestion.user_name}</p>
                  <p data-testid="review-user-email" className="text-sm"><span className="font-medium">Email:</span> {selectedQuestion.user_email}</p>
                  <p className="text-sm text-slate-500 mt-1">{new Date(selectedQuestion.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Question</label>
                <div className="bg-slate-50 rounded-md p-4">
                  <p data-testid="review-question" className="text-slate-900">{selectedQuestion.question_text}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">AI-Generated Answer</label>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p data-testid="review-ai-answer" className="text-slate-700 whitespace-pre-wrap">{selectedQuestion.ai_answer || 'Generating...'}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Your Expert Review</label>
                <Textarea
                  data-testid="review-answer-input"
                  value={reviewAnswer}
                  onChange={(e) => setReviewAnswer(e.target.value)}
                  className="min-h-48"
                  placeholder="Edit or enhance the AI answer with your expert insights..."
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  data-testid="cancel-review-btn"
                  variant="outline"
                  onClick={() => setSelectedQuestion(null)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-review-btn"
                  onClick={handleSubmitReview}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Review
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Tabs defaultValue="pending" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger data-testid="pending-tab" value="pending">Pending ({pendingQuestions.length})</TabsTrigger>
              <TabsTrigger data-testid="all-tab" value="all">All Questions ({allQuestions.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-4">
              {pendingQuestions.length === 0 ? (
                <Card className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                  <p className="text-slate-600">No pending reviews. Great job!</p>
                </Card>
              ) : (
                pendingQuestions.map((question) => (
                  <Card key={question.id} className="p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleSelectQuestion(question)}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(question.status)}
                          <span className="text-xs text-slate-500">{new Date(question.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-slate-900 font-medium mb-1">{question.user_name}</p>
                        <p className="text-sm text-slate-600 line-clamp-2">{question.question_text}</p>
                      </div>
                      <Button data-testid={`review-btn-${question.id}`} size="sm" className="shrink-0">
                        Review
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="all" className="space-y-4">
              {allQuestions.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-slate-600">No questions yet.</p>
                </Card>
              ) : (
                allQuestions.map((question) => (
                  <Card key={question.id} className="p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(question.status)}
                          <span className="text-xs text-slate-500">{new Date(question.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-slate-900 font-medium mb-1">{question.user_name}</p>
                        <p className="text-sm text-slate-600 line-clamp-2">{question.question_text}</p>
                      </div>
                      {question.status === 'pending_review' && (
                        <Button data-testid={`review-all-btn-${question.id}`} size="sm" onClick={() => handleSelectQuestion(question)}>
                          Review
                        </Button>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}